import { ExpandLessRounded, ExpandMoreRounded } from '@mui/icons-material'
import {
  alpha,
  Collapse,
  darken,
  Divider,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  Typography,
  useTheme,
} from '@mui/material'
import { useRef, useState } from 'react'
import { NavLink, useLocation } from 'react-router-dom'

import useIsPartner from '~/features/partners/hooks/useIsPartner'
import {
  ESettingsStepFriendlyNames,
  pathByFriendlyStepName,
} from '~/features/settings/models'
import { SIDE_NAV_HEADER_CLASS_NAME } from '~/layouts/account/header/nav/side/constants'
import {
  EPartnerAdminNavItemNames,
  TNavItem,
} from '~/layouts/account/hooks/useNavItems'
import { EClientPath } from '~/models/api'
import { store } from '~/store'
import rem from '~/utils/ui'

type TProps = {
  navItem: TNavItem
  close: () => void
}

function NavItem({ navItem, close }: TProps) {
  const [setUIElements, sideNavOpenSections] = store((state) => [
    state.setUIElements,
    state.UIElements.sideNavOpenSections,
  ])
  const theme = useTheme()
  const isPartner = useIsPartner()

  const [portalConfigNavHasBeenOpened, setPortalConfigNavHasBeenOpened] =
    useState<boolean>(false)
  const collapseTriggerRef = useRef(null)
  const location = useLocation()
  const { pathname } = useLocation()

  const handleCollapseClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
    if (!navItem.children?.length) {
      return
    }
    if (
      collapseTriggerRef.current &&
      (collapseTriggerRef.current as HTMLElement).contains(event.target as Node)
    ) {
      setUIElements({
        sideNavOpenSections: {
          ...sideNavOpenSections,
          [navItem.name]: !sideNavOpenSections[navItem.name],
        },
      })

      event.preventDefault()
      event.stopPropagation()
    }
  }

  const getCollapsedState = (navigationItem: TNavItem) => {
    if (navigationItem.name === EPartnerAdminNavItemNames.portalConfig) {
      const isPortalConfigNavOpen =
        !!sideNavOpenSections[EPartnerAdminNavItemNames.portalConfig]
      // when you navigate to the partner branding or general settings, the portal config nav should be open by default
      if (
        (pathname.includes(EClientPath.partnerBranding) ||
          pathname.includes(EClientPath.partnerGeneralSettings)) &&
        !isPortalConfigNavOpen &&
        !portalConfigNavHasBeenOpened
      ) {
        setPortalConfigNavHasBeenOpened(true)
        return true
      }
      return isPortalConfigNavOpen
    }

    return !!sideNavOpenSections[navigationItem.name]
  }

  if (navItem.isSectionHeader) {
    return (
      <Stack
        direction="row"
        alignItems="center"
        className={SIDE_NAV_HEADER_CLASS_NAME}
        sx={{
          pt: rem(16),
          pb: rem(10),
          px: rem(8),
          whiteSpace: 'nowrap',
          overflow: 'hidden',
        }}
      >
        <Typography
          sx={{
            fontSize: rem(11),
            letterSpacing: rem(2),
            color: alpha(theme.palette.menuFont, 0.7),
            textTransform: 'uppercase',
          }}
        >
          {navItem.name}
        </Typography>
        <Divider
          sx={{
            flexGrow: 1,
            backgroundColor: alpha(theme.palette.menuFont, 0.2),
            marginX: rem(8),
          }}
        />
      </Stack>
    )
  }

  return (
    <Stack
      spacing={0}
      key={navItem.name}
      component={navItem.path ? NavLink : 'div'}
      to={navItem.path || ''}
      className={navItem.children?.length ? 'collapsible' : ''}
      onClick={handleCollapseClick}
      sx={{
        textDecoration: 'none',
        textAlign: 'center',
        color: theme.palette.menuFont,

        '& .MuiButtonBase-root': {
          marginBottom: rem(8),
          px: rem(10),

          '&:hover svg': isPartner
            ? darken(theme.palette.menuIcon, 0.4)
            : theme.palette.common.white,
        },
        '& .MuiListItemButton-root': {
          borderTopLeftRadius: '1rem',
          borderBottomLeftRadius: '1rem',
          py: 0,
        },
        '& .MuiListItemText-root': {
          my: 0,
        },
        '& .MuiListItemText-primary': {
          ml: 1,
          textAlign: 'left',
          whiteSpace: 'nowrap',
        },
        '& .MuiTypography-root': {
          ml: rem(12),
          fontWeight: '300',
          letterSpacing: rem(2),
          fontSize: rem(14),
          lineHeight: rem(34),
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        },
        '&.active': {
          '&.collapsible, & .MuiListItemText-primary': {
            fontWeight: 'bold',
            '& svg': {
              fill: isPartner
                ? darken(theme.palette.menuFont, 0.4)
                : theme.palette.menuFont,
            },
            '& .MuiListItemButton-root.active .MuiListItemText-primary': {
              fontWeight: 'bold',
            },
          },
        },
        '& .MuiCollapse-wrapperInner .MuiButtonBase-root.MuiListItemButton-root':
          {
            marginLeft: rem(17),
            marginBottom: rem(4),
            '&:last-child': {
              marginBottom: rem(22),
            },
            '& .MuiTypography-root': {
              pl: rem(14),
              fontWeight: '300',
              fontSize: rem(15),
              letterSpacing: rem(1.7),
            },
          },
      }}
    >
      <ListItemButton
        onClick={() => {
          if (!navItem.children?.length) {
            close()
          }
        }}
        ref={collapseTriggerRef}
        className={
          navItem.children?.some(
            (child) => 'path' in child && child.path === pathname,
          )
            ? 'active'
            : ''
        }
        sx={{
          '&.active': {
            '.MuiTypography-root': {
              fontWeight: 'bold',
            },
            '& svg': {
              fill: isPartner
                ? darken(theme.palette.menuFont, 0.4)
                : theme.palette.menuFont,
            },
            '& > svg': {
              width: rem(23),
              height: rem(23),
              fill: isPartner
                ? darken(theme.palette.menuFont, 0.4)
                : theme.palette.menuFont,
              marginTop: rem(-2),
            },
          },

          '&:hover svg': {
            fill: isPartner
              ? darken(theme.palette.menuFont, 0.4)
              : theme.palette.menuFont,
          },
        }}
      >
        <ListItemIcon
          sx={{
            minWidth: 'auto',
            color: theme.palette.menuFont,
            flexGrow: 0,
            width: rem(19),
            overflow: 'hidden',
            marginTop: rem(-2),
          }}
        >
          {navItem.icon}
        </ListItemIcon>
        <ListItemText
          primary={navItem.name}
          secondary={navItem.secondaryText}
          primaryTypographyProps={{
            sx: { display: 'inline-flex' },
          }}
          secondaryTypographyProps={{
            component: 'span',
            sx: {
              color: theme.palette.menuIcon,
              textTransform: 'lowercase',
              display: 'inline-flex',
            },
          }}
        />
        {navItem.children?.length && getCollapsedState(navItem) && (
          <ExpandLessRounded />
        )}
        {navItem.children?.length && !getCollapsedState(navItem) && (
          <ExpandMoreRounded />
        )}
      </ListItemButton>
      {navItem.children?.length && (
        <Collapse in={getCollapsedState(navItem)} timeout="auto" unmountOnExit>
          <List disablePadding>
            {navItem.children?.map((child) =>
              'path' in child ? (
                <ListItemButton
                  component={NavLink}
                  className={child.path === pathname ? 'active' : ''}
                  to={child.path || ''}
                  sx={{
                    ml: 4,
                    color: theme.palette.menuFont,
                    '.MuiCollapse-wrapperInner &.active.MuiButtonBase-root.MuiListItemButton-root .MuiTypography-root ':
                      {
                        fontWeight: 'bold',
                      },
                  }}
                  onClick={close}
                  key={child.name}
                >
                  <ListItemText primary={child.name} />
                </ListItemButton>
              ) : (
                <ListItemButton
                  className={
                    pathByFriendlyStepName[
                      child.name as ESettingsStepFriendlyNames
                    ] === location.pathname
                      ? 'active'
                      : ''
                  }
                  sx={{
                    ml: 4,
                    color: theme.palette.menuFont,
                  }}
                  key={child.name}
                >
                  <ListItemText primary={child.name} />
                </ListItemButton>
              ),
            )}
          </List>
        </Collapse>
      )}
    </Stack>
  )
}

export default NavItem
